import { Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from '@tolgee/react';

import { useEffect, useRef, useState } from 'react';
import * as PropTypes from 'prop-types';
import { InputTags } from './tagsInput';
import { TopCard } from './topCard';
import { InfoCard } from './infoCard';
import { UsersLogs } from './userLogs';
import { Statistics } from './basicStatistics';
import { InteralRating } from './internalRating';
import { getImageUrl } from '../../../firebase/utils';
import ContentLoader from 'react-content-loader';
import {
  setGeneralInfo,
  setRightMenuOpen
} from '../../../redux/slices/communication';
import { POST_DETAILS } from '../constants';
import './styles.css';

// Define the custom breakpoint constant
const CUSTOM_BREAKPOINT = 1600;
const COLLASE_BREAKPOINT = 1000;

export default function GeneralInformation() {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const {
    ui: { generalInfo },
    data: { activeConversation },
    isLoading
  } = useSelector((state) => state.communication);

  // Initialize isLargeScreen state
  const [isLargeScreen, setIsLargeScreen] = useState(
    typeof window !== 'undefined' && window.innerWidth >= CUSTOM_BREAKPOINT
  );

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= CUSTOM_BREAKPOINT);
      // if the screensize is less than 1000px, close the right menu
      if (window.innerWidth < COLLASE_BREAKPOINT) {
        dispatch(setGeneralInfo(false));
      }
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // if screen is big enough, open the right menu
    if (isLargeScreen) {
      dispatch(setGeneralInfo(true));
    }
  }, [isLargeScreen, generalInfo]);
  // Move the early returns AFTER the hooks
  if (!activeConversation && !isLoading) return null;
  if (!generalInfo) return null;

  return (
    <Col
      xs={3}
      xxl={2}
      className={'shadow right-on-small'}
      style={{
        position: isLargeScreen ? 'relative' : 'absolute',
        top: 0,
        right: isLargeScreen ? '0' : '10px',
        zIndex: 106,
        maxHeight: '100vh', // Prevents the div from extending beyond the viewport height
        maxWidth: '100vw', // Prevents the div from extending beyond the viewport width
        overflowY: 'auto', // Allows vertical scrolling if content overflows
        overflowX: 'hidden' // Prevents horizontal scrolling
      }}
    >
      <div
        className="card p-3 d-flex align-items-center position-relative h-100 user-select-none"
        style={{
          scroll: 'auto',
          overflowX: 'hidden'
        }}
      >
        <div
          className={'px-2 py-2 w-100'}
          style={{ display: isLargeScreen ? 'none' : 'block' }}
        >
          <div className={'px-2 py-2 w-100'}>
            <div className={'w-100 text-start'}>
              <i
                className="fa-duotone fa-times text-muted fw-bolder"
                onClick={() => {
                  if (generalInfo) {
                    dispatch(setGeneralInfo(false));
                    dispatch(setRightMenuOpen(null));
                  }
                }}
              ></i>
            </div>
          </div>
        </div>
        <TopCard />
        <GeneralPostDetails activeConversation={activeConversation} />
        <InfoCard />
        <UsersLogs />
        <div className="card m-2 p-2 shadow d-flex justify-start w-100 align-items-center flex-row">
          <InputTags />
        </div>
        <Statistics statistics={activeConversation?.statistics} t={t} />
        <InteralRating />
      </div>
    </Col>
  );
}

export const GeneralPostDetails = ({ activeConversation }) => {
  const imgRef = useRef(null);
  const [isPortrait, setIsPortrait] = useState(false);
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageLoadDone, setImageLoadDone] = useState(false);
  const [
    imageLoadedAndAspectRatioCalculated,
    setImageLoadedAndAspectRatioCalculated
  ] = useState(false);

  useEffect(() => {
    if (imgRef.current) {
      imgRef.current.onload = () => {
        setImageLoaded(true);
      };
    }
  }, [imgRef]);

  useEffect(() => {
    // This function runs when the component unmounts
    return () => {
      imgRef.current = null;
    };
  }, []); // Empty dependency array means this effect runs once on mount and cleanup runs on unmount

  useEffect(() => {
    if (imageLoaded && !imageLoadDone) {
      setImageLoadDone(true);
      const height = imgRef.current.naturalHeight;
      const width = imgRef.current.naturalWidth;
      if (height > width) {
        setIsPortrait(true);
      }
      setImageLoadedAndAspectRatioCalculated(true);
    }
  }, [imageLoaded]);

  const [imageUrl, setImage] = useState(null);
  const [imageError, setImageError] = useState(false);
  useEffect(() => {
    if (activeConversation?.post_data?.preview_image_url) {
      setImage(activeConversation?.post_data?.preview_image_url);
    } else if (activeConversation?.post_data?.preview_image_file) {
      getImageUrl(activeConversation?.post_data?.preview_image_file).then(
        (url) => setImage(url)
      );
    }
  }, [activeConversation]);

  if (!activeConversation?.post_data) return null;
  if (
    activeConversation?.post_data?.preview_image_file ||
    activeConversation?.post_data?.preview_image_url
  )
    return (
      <div className="card m-2 p-4 shadow w-100">
        <div className="d-flex justify-content-between w-100 align-items-center">
          <b className="text-muted m-0 mb-2 p-0 fs-6 fw-bold">
            {t('communication.general_info.post_preview')}
          </b>
          {activeConversation?.post_data?.permalink_url && (
            <i
              className="fa-solid fa-up-right-from-square cursor-pointer"
              onClick={() => {
                window.open(
                  activeConversation?.post_data?.permalink_url,
                  '_blank'
                );
              }}
            ></i>
          )}
        </div>
        {!imageError && (
          <div
            className={`w-100 cursor-pointer ${
              isPortrait ? 'ratio ratio-1x1' : ''
            }`}
            onClick={() => dispatch(setRightMenuOpen(POST_DETAILS))}
          >
            <div className="d-flex justify-content-center align-items-center">
              {imageUrl && !imageError && (
                <img
                  src={imageUrl}
                  className={`rounded object-fit-scale-down m-2 ${
                    !isPortrait ? 'w-100 h-100' : ''
                  }`}
                  onError={(e) => {
                    // setImage url to null
                    setImageError(true);
                  }}
                  ref={imgRef}
                  style={{
                    height: !isPortrait ? '100%' : '95%',
                    maxWidth: '100%',
                    display: imageLoadedAndAspectRatioCalculated
                      ? 'block'
                      : 'none'
                  }}
                  onLoad={() => setImageLoaded(true)}
                />
              )}
              {!imageUrl && !imageError && <GeneralPostDetailsLoader />}
            </div>
          </div>
        )}
      </div>
    );

  if (
    activeConversation?.post_data?.text_as_html &&
    activeConversation?.post_data?.text_as_html !== ''
  )
    return (
      <div className="card m-2 p-4 shadow w-100">
        <div className="d-flex justify-content-between w-100 align-items-center">
          <b className="text-muted m-0 mb-2 p-0 fs-6 fw-bold">
            {t('communication.general_info.post_preview')}
          </b>
          {activeConversation?.post_data?.permalink_url && (
            <i
              className="fa-solid fa-up-right-from-square cursor-pointer"
              onClick={() => {
                window.open(
                  activeConversation?.post_data?.permalink_url,
                  '_blank'
                );
              }}
            ></i>
          )}
        </div>
        <div
          className="p-2 w-100 cursor-pointer overflow-hidden"
          style={{ lineClamp: 5, maxHeight: 200, overflow: 'hidden' }}
          onClick={() => dispatch(setRightMenuOpen(POST_DETAILS))}
        >
          {console.log(activeConversation?.post_data?.text_as_html)}
          <div
            dangerouslySetInnerHTML={{
              __html: activeConversation?.post_data?.text_as_html
            }}
          ></div>
        </div>
      </div>
    );
};
GeneralPostDetails.propTypes = {
  activeConversation: PropTypes.any
};

export const GeneralPostDetailsLoader = () => {
  return (
    <ContentLoader
      width={'100%'}
      viewBox="0 0 280 280"
      backgroundColor="#f0f0f0"
      foregroundColor="#dedede"
    >
      <rect x="2" y="2" rx="10" ry="10" width="280" height="280" />
    </ContentLoader>
  );
};
